import { localEndOfDayIso, localStartOfDayIso, toDate } from '@/utils/date';
import { parseFloatOrUndefined } from '@/utils/number';
import type { FilterConfig, FilterState, MatchMode } from './types';

const isBlank = (value: unknown): boolean => value === null || value === undefined || value === '';

const toCellValues = (value: unknown): unknown[] => (Array.isArray(value) ? value : [value]);

// Legacy shared-table semantics: exact matching is EXACT equality against the cell
// value — array cells match when any element is selected. Shared by multi-choice, select, and
// multi-text columns whose matchMode resolves to 'exact' (the default — see types.ts).
const matchesExact = (value: unknown, selected: string[]): boolean => toCellValues(value).some((cellValue) => selected.includes(isBlank(cellValue) ? '' : String(cellValue)));

// Legacy `text` column semantics: case-insensitive substring against the (single) selected value.
const matchesSubstring = (value: unknown, selected: string[]): boolean => {
  const needle = (selected[0] ?? '').trim().toLowerCase();
  if (!needle) return true;
  return toCellValues(value).some((cellValue) =>
    String(cellValue ?? '')
      .toLowerCase()
      .includes(needle),
  );
};

const matchesText = (value: unknown, selected: string[], matchMode: MatchMode): boolean =>
  matchMode === 'substring' ? matchesSubstring(value, selected) : matchesExact(value, selected);

const matchesRange = (value: unknown, [fromRaw, toRaw]: string[]): boolean => {
  const numeric = typeof value === 'number' ? value : parseFloatOrUndefined(String(value ?? ''));
  if (numeric === undefined) return false;
  const from = fromRaw ? parseFloatOrUndefined(fromRaw) : undefined;
  const to = toRaw ? parseFloatOrUndefined(toRaw) : undefined;
  if (from !== undefined && numeric < from) return false;
  if (to !== undefined && numeric > to) return false;
  return true;
};

const matchesDate = (value: unknown, [fromRaw, toRaw]: string[]): boolean => {
  if (isBlank(value)) return false;
  const cellTime = toDate(value as string | number | Date).getTime();
  if (fromRaw && cellTime < new Date(localStartOfDayIso(fromRaw)).getTime()) return false;
  if (toRaw && cellTime > new Date(localEndOfDayIso(toRaw)).getTime()) return false;
  return true;
};

const columnMatches = <TRow>(row: TRow, config: FilterConfig<TRow>, selected: string[] | undefined): boolean => {
  if (!selected || selected.length === 0) return true;
  const value = config.getValue(row);
  switch (config.type) {
    case 'multi-text':
      return matchesText(value, selected, config.matchMode ?? 'exact');
    case 'multi-choice':
    case 'select':
      return matchesExact(value, selected);
    case 'range':
      return matchesRange(value, selected);
    case 'date':
      return matchesDate(value, selected);
    default:
      return true;
  }
};

export const rowMatchesFilters = <TRow>(row: TRow, configs: Array<FilterConfig<TRow>>, state: FilterState): boolean =>
  configs.every((config) => columnMatches(row, config, state[config.key]));

export const applyFilters = <TRow>(rows: TRow[], configs: Array<FilterConfig<TRow>>, state: FilterState): TRow[] => rows.filter((row) => rowMatchesFilters(row, configs, state));

/** multi-choice/select options come from distinct data values, arrays flattened, sorted. */
export const deriveFilterOptions = <TRow>(rows: TRow[], config: FilterConfig<TRow>): string[] => {
  const values = new Set<string>();
  for (const row of rows) {
    const value = config.getValue(row);
    for (const entry of toCellValues(value)) {
      if (!isBlank(entry)) values.add(String(entry));
    }
  }
  return [...values].sort();
};
